(() => {
  const initialized = new WeakSet();

  class MonthlyProduct {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-monthly-form]');
      this.data = this.readData();
      this.labels = this.data.i18n || {};
      this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      this.variantInputs = Array.from(root.querySelectorAll('[data-monthly-variant]'));
      this.planInput = root.querySelector('[data-monthly-plan]');
      this.planWrap = root.querySelector('[data-monthly-plan-wrap]');
      this.price = root.querySelector('[data-monthly-price]');
      this.submit = root.querySelector('[data-monthly-submit]');
      this.status = root.querySelector('[data-monthly-status]');
      this.busy = false;
      this.bindPurchase();
      this.bindGallery();
      this.bindSizeGuide();
    }

    readData() {
      try {
        return JSON.parse(this.root.querySelector('[data-monthly-data]')?.textContent || '{}');
      } catch {
        return {};
      }
    }

    label(key, fallback) {
      return typeof this.labels[key] === 'string' ? this.labels[key] : fallback;
    }

    variant() {
      const selected = this.variantInputs.find(input => input.checked);
      return this.variants.find(variant => String(variant.id) === selected?.value);
    }

    allocations(variant) {
      return (Array.isArray(variant?.allocations) ? variant.allocations : []).filter(plan =>
        plan.recurring === true && /^\d+$/.test(String(plan.id)) && Number(plan.price) > 0
      );
    }

    selection() {
      const variant = this.variant();
      const plans = this.allocations(variant);
      const plan = plans.find(item => String(item.id) === this.planInput?.value);
      return { variant, plans, plan };
    }

    errorFor({ variant, plans, plan }) {
      if (!variant) return this.label('chooseSize', 'Choose your size.');
      if (!variant.available) return this.label('soldOut', 'This size is sold out.');
      if (!(Number(variant.price) > 0)) return this.label('unavailable', 'Monthly subscriptions are not available yet.');
      if (!plans.length) return this.label('unavailable', 'Monthly subscriptions are not available yet.');
      if (!plan) return this.label('choosePlan', 'Choose your monthly subscription.');
      return '';
    }

    rebuildPlans() {
      if (!this.planInput) return;
      const prior = this.planInput.value;
      const plans = this.allocations(this.variant());
      const options = plans.map(plan => new Option(plan.name, String(plan.id)));
      this.planInput.replaceChildren(...options);
      if (plans.some(plan => String(plan.id) === prior)) this.planInput.value = prior;
      else if (plans.length) this.planInput.value = String(plans[0].id);
      this.planInput.disabled = plans.length === 0;
      this.planInput.required = plans.length > 0;
      if (this.planWrap) this.planWrap.hidden = plans.length <= 1;
    }

    refresh() {
      const selection = this.selection();
      const message = this.errorFor(selection);
      const price = selection.plan?.priceText || (Number(selection.variant?.price) > 0
        ? selection.variant?.priceText
        : this.label('priceUnavailable', 'Price coming soon'));
      if (this.price && typeof price === 'string') this.price.textContent = price;
      if (this.status) this.status.textContent = this.busy ? this.label('submitting', 'Adding your box…') : message;
      if (this.submit) {
        this.submit.disabled = this.busy || !!message;
        const label = this.submit.querySelector('[data-monthly-submit-label]') || this.submit;
        label.textContent = this.busy
          ? this.label('submitting', 'Adding your box…')
          : this.label('join', 'Join the monthly box');
      }
      this.form?.setAttribute('aria-busy', String(this.busy));
      this.root.dataset.monthlyReady = String(!message);
    }

    bindPurchase() {
      if (!this.form) return;
      this.variantInputs.forEach(input => input.addEventListener('change', () => {
        this.busy = false;
        this.rebuildPlans();
        this.refresh();
      }));
      this.planInput?.addEventListener('change', () => this.refresh());
      this.form.addEventListener('submit', event => {
        const error = this.errorFor(this.selection());
        if (error || this.busy) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.refresh();
          if (error) this.status?.focus({ preventScroll: true });
          return;
        }
        this.busy = true;
        this.refresh();
      }, true);
      window.addEventListener('pageshow', () => {
        this.busy = false;
        this.rebuildPlans();
        this.refresh();
      });
      this.rebuildPlans();
      this.refresh();
    }

    bindGallery() {
      const mainImage = this.root.querySelector('[data-monthly-main-image]');
      const thumbs = Array.from(this.root.querySelectorAll('[data-monthly-thumb]'));
      if (!mainImage || !thumbs.length) return;
      const activate = thumb => {
        if (!thumb.dataset.src) return;
        if (thumb.dataset.sizes) mainImage.sizes = thumb.dataset.sizes;
        else mainImage.removeAttribute('sizes');
        if (thumb.dataset.srcset) mainImage.srcset = thumb.dataset.srcset;
        else mainImage.removeAttribute('srcset');
        mainImage.src = thumb.dataset.src;
        mainImage.alt = thumb.dataset.alt || '';
        ['width', 'height'].forEach(property => {
          const value = thumb.dataset[`image${property[0].toUpperCase()}${property.slice(1)}`];
          if (value && /^\d+$/.test(value) && Number(value) > 0) mainImage.setAttribute(property, value);
        });
        thumbs.forEach(item => item.setAttribute('aria-pressed', String(item === thumb)));
      };
      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => activate(thumb));
        thumb.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % thumbs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + thumbs.length - 1) % thumbs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = thumbs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          thumbs[next].focus();
          activate(thumbs[next]);
        });
      });
    }

    bindSizeGuide() {
      const dialog = this.root.querySelector('[data-monthly-size-dialog]');
      if (!dialog) return;
      let opener;
      this.root.querySelectorAll('[data-monthly-size-open]').forEach(button => {
        button.addEventListener('click', () => {
          opener = button;
          if (!dialog.open) dialog.showModal();
        });
      });
      dialog.querySelectorAll('[data-monthly-size-close]').forEach(button => {
        button.addEventListener('click', () => dialog.close());
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => opener?.focus({ preventScroll: true }));
    }
  }

  const initialize = scope => {
    const roots = scope.matches?.('[data-monthly-product]') ? [scope] : scope.querySelectorAll('[data-monthly-product]');
    roots.forEach(root => {
      if (initialized.has(root)) return;
      initialized.add(root);
      new MonthlyProduct(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
